
import { supabase } from '@/integrations/supabase/client';
import { Product, ProductStatus, globalProducts, mapDatabaseToProduct } from '@/types/product';
import { getBillingPeriod } from '@/utils/billingPeriodUtils';

// Map Supabase product data to our Product interface
export const mapProductData = (item: Record<string, unknown>): Product => {
  return {
    id: item.id as string,
    title: (item.name as string) || '', // Use name as title if available
    name: (item.name as string) || '',  // Store original name 
    description: (item.description as string) || '',
    amount: (item.price_cents as number) || 0, // Use price_cents for amount
    price_cents: (item.price_cents as number) || 0,
    status: (item.status as string) as ProductStatus || 'active', // Ensure status is of type ProductStatus
    created_at: item.created_at as string,
    billing_period: getBillingPeriod((item.billing_period as string) || 'one_time'),
    hero_image: item.hero_image as string,
    use_anchor_billing: item.use_anchor_billing as boolean,
    anchor_billing_day: item.anchor_billing_day as number,
    user_id: item.user_id as string // Ensure user_id is treated as a UUID
  };
};

// Function to fetch products from Supabase
export const fetchProducts = async (userId?: string): Promise<Product[]> => {
  try {
    // If no userId provided, get current user
    let currentUserId = userId;
    if (!currentUserId) {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user?.id) {
        console.error('No authenticated user found:', authError);
        return [];
      }
      currentUserId = user.id;
    }
    
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('user_id', currentUserId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching products:', error);
      if (error.code === 'PGRST116') {
        console.error('❌ Row-level security prevented access to products');
      }
      return [...globalProducts];
    }

    if (data && data.length > 0) {
      // Map and log all product rows for debugging
      const mappedProducts = data.map(item => mapDatabaseToProduct(item));

      // Update global cache by reference
      globalProducts.length = 0;
      globalProducts.push(...mappedProducts);

      return [...globalProducts];
    }

    console.log('⚠️ No products returned from database, using current global products');
    return [...globalProducts];
  } catch (error) {
    console.error('❌ Error in fetchProducts:', error);
    return [...globalProducts];
  }
};

// Function to get a product by ID
export const getProductById = async (id: string): Promise<Product | null> => {
  try {
    console.log("🔍 Looking for product with ID:", id);
    
    // First check local cache
    const localProduct = globalProducts.find(p => p.id === id);
    
    if (localProduct) {
      console.log("✅ Found product in local cache by ID:", localProduct);
      return localProduct;
    }
    
    // If not in local cache, try database
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (error) {
      console.error(`❌ Error fetching product by ID ${id}:`, error);
      return null;
    }
    
    if (data) {
      console.log("✅ Found product in database:", data);
      const product = mapProductData(data);
      
      // Add to global cache
      const existingIndex = globalProducts.findIndex(p => p.id === product.id);
      if (existingIndex >= 0) {
        globalProducts[existingIndex] = product;
      } else {
        globalProducts.push(product);
      }
      
      return product;
    }
    
    console.log(`❌ Product with ID ${id} not found in database`);
    return null;
  } catch (error) {
    console.error(`❌ Error in getProductById for ${id}:`, error);
    return null;
  }
};
